/**
 * TACTICAL UI (v4) - Atomic Style Factory
 * The SINGLE SOURCE OF TRUTH for our Design System.
 * 100% PIXEL-PERFECT ALIGNMENT with Peak Tactical Build (Commit 7f).
 * Stabilized for HMR and cross-platform readiness.
 */

interface CardState {
    isActive?: boolean;
    isDueNow?: boolean;
    isOverdue?: boolean;
    isPlanned?: boolean;
    isCompleted?: boolean;
    isSkipped?: boolean;
    themeColor: string;
    isExpanded?: boolean;
}

export const UI = {
    /**
     * resolveStyles: Universal Style Hub.
     * Returns statically discoverable Tailwind classes AND high-fidelity inline properties.
     */
    resolveStyles: (state: CardState) => {
        const { isActive, isDueNow, isOverdue, isPlanned, isCompleted, isSkipped, themeColor, isExpanded } = state;
        
        // 1. STATIC TAILWIND CLASSES (For compiler discovery)
        const cardClasses = `flex-col rounded-[14px] p-4 transition-all duration-150 border-t border-r border-b border-l-[5px] mb-2 ${isPlanned && !isDueNow && !isOverdue ? 'border-dashed' : 'border-solid'} ${isActive || isDueNow ? 'animate-pulse' : ''} hover-glow`;
        const podClasses = `w-[42px] h-[42px] rounded-xl items-center justify-center border shrink-0 ${isPlanned && !isDueNow && !isOverdue ? 'border-dashed' : 'border-solid'}`;
        const titleClasses = `text-white text-[15px] font-[900] uppercase tracking-[0.5px] leading-tight ${isSkipped ? 'line-through' : ''}`;
        const subtitleClasses = `text-[12px] font-[800] tracking-[0.5px] uppercase mt-1 ${isOverdue ? 'text-red-500' : 'text-[#777]'}`;
        const badgeClasses = `px-1.5 py-0.5 rounded-[4px] border flex items-center justify-center ml-1`;

        // 2. DYNAMIC PROPERTIES (For 100% visual parity)
        return {
            classes: {
                card: cardClasses,
                pod: podClasses,
                title: titleClasses,
                subtitle: subtitleClasses,
                badge: badgeClasses
            },
            props: {
                card: {
                    backgroundColor: isActive || isDueNow ? `${themeColor}22` : isOverdue ? 'rgba(239, 68, 68, 0.05)' : isCompleted ? `${themeColor}0d` : 'transparent',
                    borderLeftColor: isOverdue ? '#ef4444' : themeColor,
                    borderTopColor: isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333',
                    borderRightColor: isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333',
                    borderBottomColor: isActive || isDueNow ? themeColor : isOverdue ? '#ef4444' : isPlanned ? `${themeColor}66` : '#333',
                    boxShadow: isDueNow ? `0 0 20px ${themeColor}22` : isCompleted ? `0 4px 20px rgba(0,0,0,0.4)` : 'none',
                    opacity: isSkipped ? 0.4 : 1
                },
                pod: {
                    backgroundColor: isDueNow ? `${themeColor}33` : `${themeColor}1a`,
                    borderColor: isOverdue ? '#ef444433' : `${themeColor}33`,
                    boxShadow: isDueNow ? `0 0 15px ${themeColor}33` : `0 0 15px ${themeColor}11`
                },
                title: {
                    color: isExpanded ? themeColor : '#fff'
                },
                subtitle: {
                    color: isOverdue ? '#ef4444' : (isPlanned && !isDueNow ? themeColor : '#777')
                },
                badge: {
                    backgroundColor: isOverdue ? '#ef4444' : isDueNow ? themeColor : `${themeColor}22`,
                    borderColor: isOverdue ? '#ef4444' : isDueNow ? themeColor : `${themeColor}44`
                },
                badgeText: {
                    color: isDueNow ? '#000' : isOverdue ? '#fff' : themeColor
                }
            }
        };
    },

    // 3. CORE LAYOUT PRIMITIVES
    layout: {
        container: "app-container-v2 viewport-constrained",
        scrollable: "scrollable-content",
        header: {
            display: 'flex', 
            flexDirection: 'row', 
            alignItems: 'center', 
            height: '54px', 
            padding: '0 8px', 
            zIndex: 50, 
            position: 'relative', 
            flexShrink: 0, 
            borderBottom: '1px solid #222', 
            backgroundColor: '#000'
        } as any,
        sessionHeader: {
            backgroundColor: '#121212', 
            width: '100%', 
            maxWidth: '500px', 
            margin: '0 auto', 
            boxSizing: 'border-box'
        } as any,
        sessionHeaderGrid: {
            display: 'grid', 
            gridTemplateColumns: '1fr auto 1fr', 
            alignItems: 'center', 
            padding: '0 15px', 
            height: '54px', 
            borderBottom: '1px solid #222',
            gap: '10px'
        } as any
    },

    // 4. TACTICAL TYPOGRAPHY
    text: {
        tinyCaps: { fontSize: 9, fontWeight: '900', color: '#666', letterSpacing: 1.5, textTransform: 'uppercase' } as any,
        label: { fontSize: 11, fontWeight: '950', color: '#fff', letterSpacing: 1, textTransform: 'uppercase' } as any,
        title: { fontSize: 15, fontWeight: '900', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' } as any,
        hero: { fontSize: 28, fontWeight: '900', color: '#fff', letterSpacing: -1 } as any,
    },

    // 5. GLOBAL ACTION BANNERS
    banner: {
        performance: {
            borderWidth: 1, borderColor: '#2ecc71', padding: 12, 
            borderRadius: 12, marginTop: 10, flexDirection: 'row', 
            justifyContent: 'space-between', alignItems: 'center', 
            backgroundColor: 'rgba(46, 204, 113, 0.05)' 
        } as any,
        recovery: {
            borderWidth: 1, borderColor: '#3b82f6', padding: 12, 
            borderRadius: 12, marginTop: 10, flexDirection: 'row', 
            justifyContent: 'space-between', alignItems: 'center', 
            backgroundColor: 'rgba(59, 130, 246, 0.05)'
        } as any
    },

    // 6. INPUT PRIMITIVES
    input: {
        standard: {
            all: 'unset', 
            backgroundColor: '#0a0a0a', 
            border: '1px solid #222', 
            borderRadius: '10px', 
            padding: '12px', 
            color: '#fff', 
            fontSize: '14px', 
            width: '100%', 
            boxSizing: 'border-box'
        } as any,
        instrumental: {
            backgroundColor: 'rgba(255,255,255,0.02)', borderWidth: 1, borderColor: '#222', 
            borderRadius: 10, height: 42, color: '#fff', fontSize: 13, width: '100%', 
            fontFamily: 'ui-sans-serif, system-ui, sans-serif', textAlign: 'center', 
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        } as any
    },

    // 7. BUTTON PRIMITIVES
    button: {
        primary: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '54px',
            borderRadius: '16px',
            fontWeight: '900',
            fontSize: '16px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderWidth: '2px',
            borderStyle: 'solid'
        } as any
    }
};
